import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useSearchParams } from "react-router-dom"
import { jwtDecode } from "jwt-decode"

import {
  setCustomerId,
  setPhoneNumber,
  setToken,
} from "../../../store/slices/auth"
import { RootState } from "../../../store/store"
import { IJwtPayload } from "./interface"
import {
  QUERY_PARAMS_EXCHANGE_TOKEN_KEY,
  ROUTES_PATHS,
} from "../../../utils/constants"
import { useAuthRequest } from "../../../api/hooks/useAuthRequest2"
import { AuthResults } from "../../../api/interface"

export const useAuth = (redirectUrlProp?: string) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const initPath = useSelector((state: RootState) => state.auth.initPath)
  const [localLoading, setLocalLoading] = useState(true)

  const [searchParams] = useSearchParams()
  const exchangeToken = searchParams.get(QUERY_PARAMS_EXCHANGE_TOKEN_KEY)

  const {
    data: authData,
    isLoading,
    status,
  } = useAuthRequest(exchangeToken, () => setLocalLoading(true))

  const redirectUrl = searchParams.get("redirectUrl")

  useEffect(() => {
    if (exchangeToken === null) {
      navigate(`/${ROUTES_PATHS.error}`)
      setLocalLoading(false)
      return
    }

    if (status === "error" && !isLoading) {
      navigate(`/${ROUTES_PATHS.error}`)
      setLocalLoading(false)
      return
    }

    if (authData?.result === AuthResults.UNAUTHORIZED  || authData?.result === AuthResults.ERROR) {
      navigate(`/${ROUTES_PATHS.authError}`)
      setLocalLoading(false)
      return
    }

    if (authData?.result === AuthResults.OTP_FLOW_REQUIRED) {
      setLocalLoading(false)
      return
    }

    const jwtToken = authData?.token

    if (jwtToken) {
      const payload = jwtDecode<IJwtPayload>(jwtToken)
      dispatch(setCustomerId(payload.CustomerId))
      dispatch(setPhoneNumber(payload.PhoneNumber))
      dispatch(setToken(jwtToken))
      if (redirectUrl || redirectUrlProp) {
        navigate(decodeURIComponent(redirectUrl || redirectUrlProp || ""))
      } else {
        navigate(initPath)
      }
      setLocalLoading(false)
    }
  }, [authData?.token, status, isLoading, navigate, initPath, dispatch])

  return {
    isLoading: localLoading,
    authResult: authData,
  }
}
